import type { FeatureFlags, MeResponse, OfstedCapability, StaffRole } from '@ucp/web/lib';

/**
 * Ofsted readiness roles per capability (PRO-3140, ADR-018 PRO-3140 amendment). Mirrors the
 * server role → capability map in `apps/api/src/auth/capabilities.ts` (asserted by
 * `apps/api/test/ofstedAccessModel.test.ts`). UX only: the server re-checks every call at the
 * provision named in the path.
 */
export const OFSTED_CAPABILITY_ROLES: Readonly<Record<OfstedCapability, readonly StaffRole[]>> = {
  ofsted_readiness_read: [
    'OFSTED_COORDINATOR',
    'OFSTED_CONTRIBUTOR',
    'OFSTED_READER',
    'OPS_SITE_LEADER',
    'OPS_DIRECTOR',
  ],
  ofsted_readiness_assess: ['OFSTED_COORDINATOR', 'OFSTED_CONTRIBUTOR'],
  ofsted_readiness_coordinate: ['OFSTED_COORDINATOR'],
  ofsted_readiness_read_portfolio: ['OFSTED_READER', 'OPS_DIRECTOR'],
  ofsted_readiness_read_group: ['OFSTED_QA_LEAD', 'COO', 'CEO'],
  ofsted_framework_author: ['OFSTED_QA_LEAD'],
  ofsted_framework_approve: ['OFSTED_QA_LEAD'],
  ofsted_provision_profile_manage: ['OFSTED_QA_LEAD'],
  // PRO-3150 / ADR-018 PRO-3150 amendment — the inspection workspace.
  ofsted_inspection_read: [
    'OFSTED_COORDINATOR',
    'OFSTED_CONTRIBUTOR',
    'OFSTED_READER',
    'OPS_SITE_LEADER',
    'OPS_DIRECTOR',
  ],
  ofsted_inspection_start: ['OFSTED_COORDINATOR', 'OPS_SITE_LEADER'],
  ofsted_inspection_contribute: [
    'OFSTED_COORDINATOR',
    'OFSTED_CONTRIBUTOR',
    'OPS_SITE_LEADER',
    'OPS_DIRECTOR',
  ],
  ofsted_inspection_coordinate: ['OFSTED_COORDINATOR', 'OPS_SITE_LEADER'],
  ofsted_inspection_provisional_read: [
    'OFSTED_COORDINATOR',
    'OFSTED_READER',
    'OPS_SITE_LEADER',
    'OPS_DIRECTOR',
  ],
  ofsted_inspection_read_portfolio: ['OFSTED_READER', 'OPS_DIRECTOR'],
  ofsted_inspection_read_group: ['OFSTED_QA_LEAD', 'COO', 'CEO'],
  ofsted_inspection_support: ['OFSTED_QA_LEAD'],
};

export const OFSTED_READ_ROLES = OFSTED_CAPABILITY_ROLES.ofsted_readiness_read;
export const OFSTED_PORTFOLIO_ROLES = OFSTED_CAPABILITY_ROLES.ofsted_readiness_read_portfolio;
export const OFSTED_GROUP_ROLES = OFSTED_CAPABILITY_ROLES.ofsted_readiness_read_group;
export const OFSTED_FRAMEWORK_ROLES: readonly StaffRole[] = [
  ...new Set<StaffRole>([
    ...OFSTED_CAPABILITY_ROLES.ofsted_framework_author,
    ...OFSTED_CAPABILITY_ROLES.ofsted_framework_approve,
  ]),
];
export const OFSTED_PROFILE_ROLES = OFSTED_CAPABILITY_ROLES.ofsted_provision_profile_manage;
export const OFSTED_INSPECTION_READ_ROLES = OFSTED_CAPABILITY_ROLES.ofsted_inspection_read;
export const OFSTED_INSPECTION_PORTFOLIO_ROLES =
  OFSTED_CAPABILITY_ROLES.ofsted_inspection_read_portfolio;
export const OFSTED_INSPECTION_GROUP_ROLES = OFSTED_CAPABILITY_ROLES.ofsted_inspection_read_group;

/**
 * The Ofsted module is on when either increment is (PRO-3150 TIP-D18): readiness, the inspection
 * workspace, or both. Each task keeps its own flag.
 */
export function ofstedModuleEnabled(flags: Partial<FeatureFlags> | undefined): boolean {
  return flags?.ofstedReadinessEnabled === true || flags?.ofstedInspectionEnabled === true;
}

/** Roles that confirm Ofsted-origin issues (O33): the QA lead, or COO and CEO as fallback. */
export const OFSTED_CONFIRMER_ROLES: readonly StaffRole[] = ['OFSTED_QA_LEAD', 'COO', 'CEO'];

/** Every role with an Ofsted capability; the `/ofsted/*` RouteGuard. Never L6 (EoP). */
export const OFSTED_ROUTE_ROLES: readonly StaffRole[] = [
  ...new Set<StaffRole>(Object.values(OFSTED_CAPABILITY_ROLES).flat()),
];

/** Does the caller hold one of these roles at this school (or group-wide when `null`)? */
export function ofstedHoldsAt(
  user: Pick<MeResponse, 'roles'>,
  roles: readonly StaffRole[],
  schoolId: string | null,
): boolean {
  return user.roles.some(
    (r) => roles.includes(r.role) && (schoolId === null || r.schoolId === schoolId),
  );
}

/** Does the caller hold one of these roles anywhere? */
export function ofstedHoldsAny(
  user: Pick<MeResponse, 'roles'>,
  roles: readonly StaffRole[],
): boolean {
  return user.roles.some((r) => roles.includes(r.role));
}
